/**
 * Audit logger — PouchDB-backed, push-only replication to CouchDB `logs`.
 *
 * Records app events (auth, data mutations, caught render errors) with
 * Monolog-style severity. PouchDB is already an IndexedDB-backed local store with
 * automatic, batched, retrying, offline-tolerant replication, so there is NO manual
 * buffer / flush timer / retry queue here — just `logsDb.put()` (durable local write)
 * and one continuous one-way `replicate.to()` that ships logs on its own.
 *
 * Rules:
 *  - Never throws. Every failure goes to console.warn only.
 *  - Fire-and-forget: callers usually don't await (except logger.flush() before logout).
 */
import { pouches_db, localStorageAuthv2, localStorageToken, localPouchPrefix } from "../config";
import { getCouchUrl } from "./getCouchUrl";
// NOTE: pouchdb-browser is imported DYNAMICALLY inside the browser guard below so
// this module can be imported in the (node) test runner without pulling in
// IndexedDB. Everything else (identity context) is read synchronously from
// localStorage — no redux store dependency. Keep the top-level imports light.

// debug → info → warning → error
const LEVELS = { debug: 0, info: 1, warning: 2, error: 3 };

// Only levels at or above VITE_LOG_LEVEL are written. Default: log everything.
const envLevel = import.meta.env.VITE_LOG_LEVEL;
const THRESHOLD = envLevel in LEVELS ? envLevel : "debug";

// localStorage key holding the current cuadre id (set in Login.jsx / Logout.jsx).
const LOCAL_CUADRE_ID = "localCommonId";

// Max time purgeLocal() will wait for its pre-wipe push before giving up (it is
// awaited on the login path, so this bounds any login-spinner latency).
const PURGE_FLUSH_TIMEOUT_MS = 8000;

/** Pure, testable: is `level` at or above `threshold`? */
export function shouldLog(level, threshold) {
  return LEVELS[level] >= LEVELS[threshold];
}

/** Short random suffix so same-millisecond ids don't collide. */
function rand() {
  return Math.random().toString(36).slice(2, 7).padEnd(5, "0");
}

/**
 * Time-ordered + unique id. ISO-8601 UTC timestamps sort lexicographically in
 * chronological order, so `${created_at}_${rand}` collates logs by time.
 * Exported for testing.
 */
export function makeLogId(created_at) {
  return `${created_at}_${rand()}`;
}

/* -------------------------------------------------------------------------- */
/* Identity context — the single "who / where" swap point.                    */
/* All read SYNCHRONOUSLY from localStorage (+ the live store for collect_date)*/
/* so a write never blocks. These fields are only populated AFTER login; a log */
/* emitted pre-login carries null for them, which is expected.                 */
/* -------------------------------------------------------------------------- */

function readAuth() {
  try {
    return JSON.parse(localStorage.getItem(localStorageAuthv2) || "null");
  } catch {
    return null;
  }
}

// collect_date is embedded in the sync prefix the app writes at login:
//   `w{owner_id}_r{creditor_id}_{collect_date}_c{cuadre_id}`  (localStorage: syncPrefix)
// Read it synchronously from there — no redux store dependency.
function readCollectDate() {
  try {
    const prefix = localStorage.getItem(localPouchPrefix);
    if (!prefix) return null;
    const parts = prefix.split("_"); // [w{owner}, r{creditor}, {collect_date}, c{cuadre}]
    return parts.length >= 4 ? parts[2] : null;
  } catch {
    return null;
  }
}

function getContext() {
  const auth = readAuth();
  const user = auth?.user ?? null;

  return {
    user_id: user?.user_id ?? null,
    device_id: localStorage.getItem(localStorageToken) ?? null,
    owner_id: user?.owner_id ?? null,
    creditor_id: user?.creditor_id ?? null,
    cuadre_id: localStorage.getItem(LOCAL_CUADRE_ID) ?? null,
    collect_date: readCollectDate(),
  };
}

/* -------------------------------------------------------------------------- */
/* Local DB + replication (browser only; never under the test runner).        */
/* -------------------------------------------------------------------------- */

const isBrowser = import.meta.env.MODE !== "test" && typeof window !== "undefined";

let PouchDBRef = null; // resolved dynamic import, reused when recreating the DB
let logsDb = null;
let remoteLogsUrl = null;
let liveRep = null; // handle to the continuous replication so we can cancel it

// Start continuous, push-only, self-retrying replication local -> remote `logs`.
// NOTE: audit logs are write-once, so auto_compaction reclaims nothing here; local
// growth is bounded instead by purgeLocal() (see below), triggered at login.
function startReplication() {
  if (!logsDb || !remoteLogsUrl) return;
  liveRep = logsDb
    .replicate.to(remoteLogsUrl, { live: true, retry: true })
    .on("change", (info) => console.debug("[logger] replicated:", info.docs_written, "docs"))
    .on("paused", (err) => {
      // paused with an error = offline / server unreachable; without = caught up
      if (err) console.warn("[logger] replication paused (will retry):", err);
    })
    .on("denied", (e) => console.warn("[logger] replication denied (auth/validation):", e))
    .on("error", (e) => console.warn("[logger] replication error:", e));
}

// Small in-memory de-dupe so a render loop / repeated rejection can't flood the log.
const recentErrorKeys = new Map(); // key -> lastLoggedMs
function shouldLogGlobalError(key) {
  const now = Date.now();
  if (now - (recentErrorKeys.get(key) || 0) < 5000) return false; // same error within 5s
  if (recentErrorKeys.size > 100) recentErrorKeys.clear(); // bound growth
  recentErrorKeys.set(key, now);
  return true;
}

// Capture the errors the React ErrorBoundary CANNOT see: uncaught errors and
// unhandled promise rejections from async event handlers (where most of this
// app's logic lives). Registered once, synchronously, at module load.
function installGlobalErrorHandlers() {
  window.addEventListener("error", (event) => {
    if (!event.error && !event.message) return; // ignore resource-load (img/script) 404s
    const key = `err:${event.message || ""}:${event.lineno || ""}`;
    if (!shouldLogGlobalError(key)) return;
    write("error", "app.window_error", "Uncaught JS error", {
      context: {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        col: event.colno,
        stack: event.error?.stack,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const msg = reason?.message || String(reason);
    if (!shouldLogGlobalError(`rej:${msg}`)) return;
    write("error", "app.unhandled_rejection", "Unhandled promise rejection", {
      context: { reason: msg, stack: reason?.stack },
    });
  });
}

if (isBrowser) {
  installGlobalErrorHandlers();

  (async () => {
    try {
      const mod = await import("pouchdb-browser");
      PouchDBRef = mod.default;

      logsDb = new PouchDBRef(pouches_db.AUDIT_LOG, { auto_compaction: true });

      // Reuse the app's exact remote + admin credentials (getCouchUrl returns a
      // base URL with a trailing slash). Remote CouchDB database: `audit_logs`.
      remoteLogsUrl = `${getCouchUrl()}audit_logs`;
      startReplication();
    } catch (e) {
      console.warn("[logger] init failed:", e);
    }
  })();
}

async function write(level, action, message, options = {}) {
  if (!shouldLog(level, THRESHOLD)) return;
  if (!logsDb) return; // not in a browser / test runner — no-op
  try {
    const created_at = new Date().toISOString();
    await logsDb.put({
      _id: makeLogId(created_at),
      type: "log", // discriminator (harmless in a dedicated DB, useful if ever merged)
      level,
      action,
      entity: options.entity ?? null,
      entity_id: options.entityId ?? null,
      message: message ?? null,
      context: options.context ?? {}, // extra data — NEVER secrets/passwords/tokens
      ...getContext(), // user_id, device_id, owner_id, creditor_id, cuadre_id, collect_date
      today_date: created_at.slice(0, 10), // calendar date the log was written (yyyy-mm-dd)
      created_at,
    });
  } catch (err) {
    console.warn("[logger] failed to store log:", err); // NEVER throw
  }
}

/** Best-effort one-shot push so pending logs reach the server before logout/close. */
function flushOnce() {
  if (!logsDb || !remoteLogsUrl) return Promise.resolve();
  return logsDb
    .replicate.to(remoteLogsUrl)
    .catch((e) => console.warn("[logger] flush error:", e));
}

/**
 * Bound local growth by wiping the on-device log DB — call at LOGIN.
 * Audit logs are append-only and never deleted locally by normal operation, so
 * the local PouchDB would grow forever. This:
 *   1. pushes every local log to the server and WAITS for it to fully succeed,
 *   2. only then cancels live replication and destroy()s the local DB — a
 *      LOCAL-ONLY wipe that is NOT replicated, so the server's `logs` survive,
 *   3. recreates the DB and restarts replication.
 *
 * IMPORTANT: destroy() deletes ALL local docs regardless of sync state, so it is
 * gated on a SUCCESSFUL push. If the push fails (offline, server down, `logs` DB
 * missing), we do NOT destroy — un-synced logs are kept and retried next login /
 * by the live replication. Never throws.
 */
async function purgeLocal() {
  if (!logsDb || !PouchDBRef || !remoteLogsUrl) return;
  try {
    // One-shot push, BOUNDED by a timeout so this can never stall the caller — it
    // is awaited on the login path. If the push doesn't finish in time, keep the
    // local logs (the live replication will drain them) and skip the wipe.
    const rep = logsDb.replicate.to(remoteLogsUrl);
    const result = await Promise.race([
      rep,
      new Promise((resolve) => setTimeout(() => resolve(null), PURGE_FLUSH_TIMEOUT_MS)),
    ]);

    // Only wipe if the server accepted EVERYTHING. A timeout (null),a non-ok
    // result, or doc_write_failures > 0 means some logs aren't safely on the
    // server yet — destroying them locally would lose them, so keep the local copy.
    if (!result || result.ok !== true || result.doc_write_failures) {
      try {
        rep.cancel(); // stop the one-shot push if it timed out / is still running
      } catch (_) {
        // ignore
      }
      return;
    }

    if (liveRep) {
      liveRep.cancel();
      liveRep = null;
    }
    await logsDb.destroy(); // local-only; destroy is not replicated to the server
    logsDb = new PouchDBRef(pouches_db.AUDIT_LOG, { auto_compaction: true });
    startReplication();
  } catch (e) {
    // Push failed / timed out -> keep local logs, skip the wipe, retry next cuadre.
    console.warn("[logger] purgeLocal skipped:", e);
    // If we cancelled live replication but then failed to recreate it, restart so
    // future logs keep shipping (self-heal without waiting for the next reload).
    if (!liveRep && logsDb) startReplication();
  }
}

export const logger = {
  debug: (action, message, options) => write("debug", action, message, options),
  info: (action, message, options) => write("info", action, message, options),
  warning: (action, message, options) => write("warning", action, message, options),
  error: (action, message, options) => write("error", action, message, options),
  flush: () => flushOnce(),
  purgeLocal: () => purgeLocal(),
};

export default logger;
